import createClient from 'openapi-fetch';
import type { paths } from './generated/schema';
import { API_BASE_URL } from '../config/api';
import { env } from '../config/env';
import { getIdToken, getSessionId } from '../store/auth-store';

type ClientOptions = {
  withAuth: boolean;
};

const buildApiClient = ({ withAuth }: ClientOptions) =>
  createClient<paths>({
    baseUrl: API_BASE_URL,
    fetch: async (input, init = {}) => {
      const token = getIdToken();
      const sessionId = getSessionId();
      const headers = new Headers(init.headers ?? {});
      const isFormData = init.body instanceof FormData;

      if (withAuth && token) {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        headers.delete('Authorization');
      }

      if (sessionId) {
        headers.set('X-Session-Id', sessionId);
      } else {
        headers.delete('X-Session-Id');
      }

      if (env.rateLimitBypassToken) {
        headers.set('X-Test-Bypass-Rate-Limit', env.rateLimitBypassToken);
      } else {
        headers.delete('X-Test-Bypass-Rate-Limit');
      }

      if (!headers.has('Content-Type') && !isFormData) {
        headers.set('Content-Type', 'application/json');
      }

      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }

      const requestInit = {
        ...init,
        headers,
      } as globalThis.RequestInit;

      if (!requestInit.cache) {
        requestInit.cache = 'no-store';
      }

      return fetch(input, requestInit);
    },
  });

export const apiClient = buildApiClient({ withAuth: true });
export const publicApiClient = buildApiClient({ withAuth: false });

export type ApiClient = typeof apiClient;
